import * as React from "react";
import { isEqual } from "lodash";
import { IAction, SagaService } from "front-end-lib/core";
import { withMddStudy } from "../../higher-order";
import { withMddLookups } from "../../higher-order/WithMddLookups/WithMddLookups";
import { MddDataContainer, MddDialog, MddDialogButtons } from "../../common";
import {
  IMddStudyProps,
  IStudyQualification,
  IStudyScale,
  StudyQualification,
  ILearningPlan
} from "../../../model";
import { ILookup } from "../../../model/interfaces/iLookup";

import { eStudySaga } from "../../../sagas";
import { isLoading, sortObjectArray } from "../../../utils";
import { MddStudyQualificationDeleteDialog } from "./MddStudyQualificationDeleteDialog";
import { MddShowStudyQualificationsTable } from "./MddShowStudyQualificationsTable";
import { MddStudyQualificationAddEditContent } from "./MddStudyQualificationAddEditContent";

interface IMddShowStudyQualificationsProps extends IMddStudyProps {
  qualificationTypes: ILookup[];
}
const _MddShowStudyQualifications = (
  props: IMddShowStudyQualificationsProps
) => {
  const {
    study,
    match: {
      params: { id: studyId }
    },
    qualificationTypes
  } = props;

  const [studyQuals, setStudyQuals] = React.useState<
    IStudyQualification[] | undefined
  >(undefined);
  const [scales, setScales] = React.useState<IStudyScale[]>([]);
  const [learningPlans, setLearningPlans] = React.useState<ILearningPlan[]>([]);
  const [mode, setMode] = React.useState<"View" | "Add" | "Edit" | "Delete">(
    "View"
  );
  const [qual, setQual] = React.useState<IStudyQualification>(
    new StudyQualification()
  );
  const [origEditQual, setOrigEditQual] = React.useState<IStudyQualification>(
    new StudyQualification()
  );

  const loadingSave = isLoading(
    `undefined_${eStudySaga.SAVE_STUDY_QUALIFICATION}`
  );

  const loadingQualPlans =
    isLoading(`undefined_${eStudySaga.FETCH_STUDY_QUALIFICATION}`) ||
    isLoading(`undefined_${eStudySaga.FETCH_LEARNING_PLANS}`);

  const setStudyQualification = (studyQualification: IStudyQualification) => {
    setQual(Object.assign(studyQualification, { studyId }));
  };

  let initialStudyQuals = React.useRef<IStudyQualification[]>([]);
  let noRecordsMessage = React.useRef<string | undefined>(undefined);
  const modeRef = React.useRef(mode);
  const qualRef = React.useRef(qual);
  qualRef.current = qual;

  const getFormattedQuals = (quals: IStudyQualification[]) => {
    return (quals || []).map((q: IStudyQualification) => {
      return Object.assign(q, {
        qual: q,
        key: q.StudyQualificationId
      });
    });
  };

  React.useEffect(() => {
    const removeStudyQual = SagaService.subscribeToSaga(
      eStudySaga.FETCH_STUDY_QUALIFICATIONS,
      (action: IAction) => {
        const { type, payload } = action;
        if (type === eStudySaga.FETCH_STUDY_QUALIFICATIONS_SUCCESS) {
          const formattedPayload = getFormattedQuals(payload);
          initialStudyQuals.current = formattedPayload;
          setStudyQuals(formattedPayload);
          if (formattedPayload && formattedPayload.length === 0) {
            noRecordsMessage.current =
              "There are no study qualifications associated with this study.";
          }
        }
      }
    );

    SagaService.dispatchSaga({
      type: eStudySaga.FETCH_STUDY_QUALIFICATIONS,
      payload: studyId
    });

    const removeStudyScales = SagaService.subscribeToSaga(
      eStudySaga.FETCH_STUDY_SCALES,
      (action: IAction) => {
        const { type, payload } = action;
        if (type === eStudySaga.FETCH_STUDY_SCALES_SUCCESS) {
          setScales(sortObjectArray(payload, "name"));
        }
      }
    );

    SagaService.dispatchSaga({
      type: eStudySaga.FETCH_STUDY_SCALES,
      payload: studyId
    });

    const removeAddQual = SagaService.subscribeToSaga(
      eStudySaga.SAVE_STUDY_QUALIFICATION,
      (action: IAction) => {
        const { type } = action;
        if (type === eStudySaga.SAVE_STUDY_QUALIFICATION_SUCCESS) {
          setMode("View");
          setStudyQualification(new StudyQualification());
          SagaService.dispatchSaga({
            type: eStudySaga.FETCH_STUDY_QUALIFICATIONS,
            payload: studyId
          });
        }
      }
    );

    const removeDeleteQual = SagaService.subscribeToSaga(
      eStudySaga.DELETE_STUDY_QUALIFICATION,
      (action: IAction) => {
        const { type } = action;
        if (type === eStudySaga.DELETE_STUDY_QUALIFICATION_SUCCESS) {
          setMode("View");
          SagaService.dispatchSaga({
            type: eStudySaga.FETCH_STUDY_QUALIFICATIONS,
            payload: studyId
          });
        }
      }
    );

    const removeLearningPlans = SagaService.subscribeToSaga(
      eStudySaga.FETCH_LEARNING_PLANS,
      (action: IAction) => {
        const { type, payload } = action;
        if (payload) {
          if (type === eStudySaga.FETCH_LEARNING_PLANS_SUCCESS) {
            // Need to convert integer ID to string for our dropdown.  Also, sort alphabetically.
            const lps = payload.map((lp: any) => {
              return { id: lp.id.toString(), name: lp.name };
            });

            setLearningPlans(sortObjectArray(lps, "name"));
          }
        }
      }
    );

    const removeFetchStudyQualification = SagaService.subscribeToSaga(
      eStudySaga.FETCH_STUDY_QUALIFICATION,
      (action: IAction) => {
        const { type, payload } = action;
        if (type === eStudySaga.FETCH_STUDY_QUALIFICATION_SUCCESS) {
          const lps = payload.learningPlans;
          const newQual: IStudyQualification = Object.assign(
            new StudyQualification(),
            {
              ...payload,
              StudyQualificationId: qualRef.current.StudyQualificationId,
              LearningPlans: lps.map((lp: any) => {
                return { id: lp.id.toString(), name: lp.name };
              })
            }
          );
          setStudyQualification(newQual);
          setOrigEditQual(newQual);
        }
      }
    );

    SagaService.dispatchSaga({
      type: eStudySaga.FETCH_LEARNING_PLANS,
      payload: studyId
    });

    return () => {
      removeStudyQual();
      removeAddQual();
      removeStudyScales();
      removeDeleteQual();
      removeLearningPlans();
      removeFetchStudyQualification();
    };
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (mode === "Edit" && modeRef.current !== "Edit") {
      SagaService.dispatchSaga({
        type: eStudySaga.FETCH_STUDY_QUALIFICATION,
        payload: qual
      });
    }
    if (mode !== modeRef.current) {
      modeRef.current = mode;
    }
  }, [qual, mode]);

  const isSubmitDisabled = () => {
    if (!qual.QualificationName || loadingSave) {
      return true;
    }

    if (mode === "Edit") {
      return isEqual(origEditQual, qual);
    }

    return false;
  };

  const onEdit = (qual: IStudyQualification) => {
    setStudyQualification(qual);
    setMode("Edit");
  };

  const onDelete = (qual: IStudyQualification) => {
    setStudyQualification(qual);
    setMode("Delete");
  };

  return (
    <MddDataContainer
      id="QualificationsData"
      breadcrumbs={[
        { display: "All Studies", route: "/study" },
        { display: study.StudyName }
      ]}
      addAction={{
        display: "Add Qualification",
        action: () => {
          setStudyQualification(new StudyQualification());
          setMode("Add");
        }
      }}
      title="Qualifications"
    >
      <MddShowStudyQualificationsTable
        studyQuals={studyQuals}
        noRecordsMessage={noRecordsMessage.current}
        initialStudyQuals={initialStudyQuals}
        setStudyQuals={setStudyQuals}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {(mode === "Add" || mode === "Edit") && (
        <MddDialog
          submitting={loadingSave || loadingQualPlans}
          showCloseButton
          onCloseClick={() => {
            setMode("View");
            setStudyQualification(new StudyQualification());
          }}
          dialogProps={{
            id: "divStudyQualDialog",
            open: true,
            maxWidth: "md",
            fullWidth: true
          }}
          dialogActionProps={{ id: "divStudyQualDialogActions" }}
          dialogContentProps={{
            id: "divStudyQualDialogContent",
            className:
              "mdd-dialog--content-100percent mdd-qualification--content"
          }}
          dialogTitleProps={{
            id: "divStudyQualDialofTitle",
            title:
              mode === "Edit"
                ? "Edit Study Qualification"
                : "Add Study Qualification"
          }}
          dialogContent={
            <MddStudyQualificationAddEditContent
              qual={qual}
              initialStudyQuals={initialStudyQuals}
              setStudyQualification={setStudyQualification}
              qualificationTypes={qualificationTypes}
              scales={scales}
              learningPlans={learningPlans}
              mode={mode}
              loadingSave={loadingSave}
              loadingQualPlans={loadingQualPlans}
            />
          }
          dialogActions={
            <MddDialogButtons
              saveButtonProps={{
                disabled: isSubmitDisabled(),
                onClick: () =>
                  SagaService.dispatchSaga({
                    type: eStudySaga.SAVE_STUDY_QUALIFICATION,
                    payload: qual
                  })
              }}
              closeButtonProps={{
                onClick: () => {
                  setMode("View");
                  setStudyQualification(new StudyQualification());
                }
              }}
            />
          }
        />
      )}
      {mode === "Delete" && (
        <MddStudyQualificationDeleteDialog
          qual={qual}
          open={true}
          onClose={() => {
            setMode("View");
            setStudyQualification(new StudyQualification());
          }}
        />
      )}
    </MddDataContainer>
  );
};

export const MddShowStudyQualifications = withMddLookups([
  "qualificationTypes"
])(withMddStudy()(_MddShowStudyQualifications));
